/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { IMasonryCardBase } from '@app/app/shared/directives/masonry.directive';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ViewMoment } from '../../../../../../../app-frame/store/app-frame.actions';
import { AppFrameState } from '../../../../../../../app-frame/store/app-frame.state';
import { V3MomentCardCommentsComponent } from '../../../../../../../shared/components/v3-moment-card-comments/v3-moment-card-comments.component';
import { LanguageCodeHelper } from '../../../../../../../shared/helpers/language-code-helper';
import { UserDetails } from '../../../../../../../user-auth/models';
import { UserAuthState } from '../../../../../../../user-auth/store/user-auth.state';
import { MomentCard } from '../../../../../models';
import { DisableFolioSectionItemVisibility, EnableFolioSectionItemVisibility } from '../../../../../store/folio.action';
import { FolioSectionItemRequest, FolioSectionItemType } from '../../../../../store/folio.state.model';
import { DeleteMoment } from '../../../../../store/moment/moment-view-all/moment-view-all.actions';
import { FolioPublicMomentContentComponent } from '../../../public-moment/public-moment-content.component';

@Component({
  selector: 'ptl-v3-folio-masonry-card',
  templateUrl: './v3-folio-masonry-card.component.html',
  styleUrls: ['./v3-folio-masonry-card.component.scss'],
})
export class V3FolioMasonryCardComponent implements IMasonryCardBase {
  /** Card data. */
  @Input() card: MomentCard;
  /** define if moment should be archived. */
  @Input() archiveMoment: boolean;
  @Input() selected;
  @Input() uid: string;
  @Input() editMode: boolean;
  @Input() isFolioSection: boolean;

  @Output() cardClick = new EventEmitter<MomentCard>();
  @Output() removeCard = new EventEmitter<string>();
  @Output() likeMoment = new EventEmitter<string>();
  @Output() unlikeMoment = new EventEmitter<string>();

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  @Select(AppFrameState.isMobile)
  isMobile$: Observable<boolean>;

  constructor(
    private store: Store,
    private dialog: MatDialog,
  ) {}

  onRemoveMoment(id: string) {
    this.removeCard.emit(id);
  }

  onArchiveMoment(id: string) {
    this.store.dispatch(new DeleteMoment(id));
  }

  onItemVisibilityState(card: MomentCard, event: Event) {
    event.stopPropagation();

    const request: FolioSectionItemRequest = {
      type: FolioSectionItemType.MOMENTS,
      uid: card._id,
    };

    if (card.visible) {
      this.store.dispatch(new DisableFolioSectionItemVisibility(this.uid, request));
    } else {
      this.store.dispatch(new EnableFolioSectionItemVisibility(this.uid, request));
    }

    card.visible = !card.visible;
  }

  onLikeMoment(event: Event) {
    event.stopPropagation();

    if (this.card.likes?.liked) {
      this.unlikeMoment.emit(this.card._id);
    } else {
      this.likeMoment.emit(this.card._id);
    }
  }

  openCommentsDialog(event: Event): void {
    event.stopPropagation();

    const isMobile = this.store.selectSnapshot(AppFrameState.isMobile);

    const dialogRef = this.dialog.open(V3MomentCardCommentsComponent, {
      width: isMobile ? '100vw' : '700px',
      height: isMobile ? '100vh' : 'auto',
      maxHeight: isMobile ? '100vh' : '92vh',
      maxWidth: isMobile ? '100vw' : '700px',
      position: isMobile
        ? {}
        : {
            top: '10vh',
          },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      restoreFocus: true,
      panelClass: ['ptl-mat-dialog', 'comments-dialog'],
      backdropClass: 'dialog-backdrop',
      data: {
        momentId: this.card._id,
        initialCommentsCount: this.card.commentsCount || 0,
      },
    });

    dialogRef.componentInstance.commentCountUpdate.subscribe((count: number) => {
      this.card.commentsCount = count;
    });
  }

  openMomentForView() {
    if (this.card.isPublicFolio) {
      this.dialog.open(FolioPublicMomentContentComponent, {
        width: '75rem',
        maxHeight: '80vh',
        position: {
          top: '10vh',
        },
        direction: LanguageCodeHelper.getBodyLanguageDir(),
        restoreFocus: true,
        panelClass: 'ptl-mat-dialog',
        backdropClass: 'dialog-backdrop',
        data: {
          moment: this.card,
        },
      });
      return;
    }
    if (this.card.viewType !== 'COLLECTOR' && !this.card.isDisabled) {
      this.store.dispatch(new ViewMoment(this.card._id));
    }
  }

  onButtonClick() {
    this.cardClick.emit(this.card);
  }
}
